import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { type Producer } from '../../../shared/types';
import { validarCuit } from '../../../shared/utils';

interface ProducerModalProps {
  isOpen: boolean;
  onClose: () => void;
  producer?: Producer | null; // If null, it's "create mode"
  onSave: (producer: Partial<Producer>) => void;
  onDelete?: (id: number | string) => void;
  onToggleStatus?: (id: number | string) => void;
}

export const ProducerModal: React.FC<ProducerModalProps> = ({ 
  isOpen, 
  onClose, 
  producer, 
  onSave,
  onDelete,
  onToggleStatus
}) => {
  if (!isOpen) return null;

  const isEditMode = !!producer;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cuit, setCuit] = useState('');
  const [phone, setPhone] = useState('');
  const [balance, setBalance] = useState<number | string>('');
  const [overdraft, setOverdraft] = useState<number | string>('');
  const [soja, setSoja] = useState<number | string>('');
  const [maiz, setMaiz] = useState<number | string>('');
  const [trigo, setTrigo] = useState<number | string>('');
  const [sorgo, setSorgo] = useState<number | string>('');
  const [girasol, setGirasol] = useState<number | string>('');
  const [mani, setMani] = useState<number | string>('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setName(producer?.name || '');
    setEmail(producer?.email || '');
    setCuit(producer?.cuit || '');
    setPhone(producer?.phone || '');
    setBalance(producer?.balance !== undefined ? producer.balance : '');
    setOverdraft(producer?.overdraft !== undefined ? producer.overdraft : '');
    setSoja(producer?.soja !== undefined ? producer.soja : '');
    setMaiz(producer?.maiz !== undefined ? producer.maiz : '');
    setTrigo(producer?.trigo !== undefined ? producer.trigo : '');
    setSorgo(producer?.sorgo !== undefined ? producer.sorgo : '');
    setGirasol(producer?.girasol !== undefined ? producer.girasol : '');
    setMani(producer?.mani !== undefined ? producer.mani : '');
    setError('');
    setIsSaving(false);
  }, [producer, isOpen]);

  const formatCuit = (value: string) => {
    // Strip everything except digits
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
  };

  const handleSaveClick = async () => {
    setError('');
    
    if (!name.trim()) {
      setError('El nombre completo es requerido.');
      return;
    }
    if (!email.trim()) {
      setError('El email es requerido.');
      return;
    }
    if (!cuit.trim()) {
      setError('El CUIT es requerido.');
      return;
    }
    
    if (!validarCuit(cuit)) {
      setError('El CUIT ingresado no es válido.');
      return;
    }

    try {
      setIsSaving(true);
      await onSave({
        id: producer?.id,
        name,
        email,
        cuit,
        phone,
        balance: Number(balance) || 0,
        overdraft: Number(overdraft) || 0,
        soja: Number(soja) || 0,
        maiz: Number(maiz) || 0,
        trigo: Number(trigo) || 0,
        sorgo: Number(sorgo) || 0,
        girasol: Number(girasol) || 0,
        mani: Number(mani) || 0,
        status: producer?.status || 'active'
      });
      onClose();
    } catch (e: any) {
      setError(e.message || 'Error al guardar el productor');
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" 
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[95vh] flex flex-col overflow-hidden" 
        style={{ borderTop: '3px solid #006837' }}
      >
        <div className="p-4 border-b border-border flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-tight">
              {isEditMode ? 'Editar productor' : 'Agregar productor'}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isEditMode ? 'Modificá los datos del productor' : 'Completá los datos del nuevo productor'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-muted transition" aria-label="Cerrar">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {error && (
            <div className="p-3 text-xs bg-red-50 text-red-600 rounded-lg border border-red-100 animate-shake font-semibold">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold mb-1">Nombre completo</label>
            <input 
              type="text" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Juan García" 
              className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm" 
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Email</label>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="juan@ejemplo.com" 
              disabled={isEditMode}
              className={`w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm ${isEditMode ? 'bg-muted cursor-not-allowed text-muted-foreground' : ''}`} 
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">CUIT</label>
              <input 
                type="text" 
                value={cuit}
                onChange={(e) => setCuit(formatCuit(e.target.value))}
                placeholder="Ej: 20-12345678-9" 
                maxLength={13}
                disabled={isEditMode}
                className={`w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm ${isEditMode ? 'bg-muted cursor-not-allowed text-muted-foreground' : ''}`} 
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Teléfono</label>
              <input 
                type="tel" 
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="11896 ..." 
                className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm" 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Saldo actual</label>
              <input 
                type="number" 
                min="0"
                value={balance}
                onChange={(e) => setBalance(Math.max(0, parseFloat(e.target.value) || 0))}
                placeholder="0" 
                disabled={isEditMode}
                className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed" 
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Límite descubierto</label>
              <input 
                type="number" 
                min="0"
                value={overdraft}
                onChange={(e) => setOverdraft(Math.max(0, parseFloat(e.target.value) || 0))}
                placeholder="0" 
                className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm" 
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Soja (tn)</label>
              <input 
                type="number" 
                step="0.1"
                min="0"
                value={soja}
                onChange={(e) => setSoja(Math.max(0, parseFloat(e.target.value) || 0))}
                placeholder="0" 
                className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm" 
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Maíz (tn)</label>
              <input 
                type="number" 
                step="0.1"
                min="0"
                value={maiz}
                onChange={(e) => setMaiz(Math.max(0, parseFloat(e.target.value) || 0))}
                placeholder="0" 
                className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm" 
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Trigo (tn)</label>
              <input 
                type="number" 
                step="0.1"
                min="0"
                value={trigo}
                onChange={(e) => setTrigo(Math.max(0, parseFloat(e.target.value) || 0))}
                placeholder="0" 
                className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm" 
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Sorgo (tn)</label>
              <input 
                type="number" 
                step="0.1"
                min="0"
                value={sorgo}
                onChange={(e) => setSorgo(Math.max(0, parseFloat(e.target.value) || 0))}
                placeholder="0" 
                className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm" 
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Girasol (tn)</label>
              <input 
                type="number" 
                step="0.1"
                min="0"
                value={girasol}
                onChange={(e) => setGirasol(Math.max(0, parseFloat(e.target.value) || 0))}
                placeholder="0" 
                className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm" 
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Maní (tn)</label>
              <input 
                type="number" 
                step="0.1"
                min="0"
                value={mani}
                onChange={(e) => setMani(Math.max(0, parseFloat(e.target.value) || 0))}
                placeholder="0" 
                className="w-full px-3 py-1.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm" 
              />
            </div>
          </div>

          {isEditMode && (
            <div className="pt-3 border-t border-border flex gap-2">
              <button 
                onClick={() => onToggleStatus && onToggleStatus(producer.id)}
                className={`flex-1 px-3 py-2 border rounded-lg transition text-xs font-semibold ${
                  producer.status === 'active' 
                    ? 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100' 
                    : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                }`}
              >
                {producer.status === 'active' ? 'Inactivar' : 'Activar'}
              </button>
              <button 
                onClick={() => onDelete && onDelete(producer.id)}
                className="flex-1 px-3 py-2 rounded-lg transition text-xs font-semibold" 
                style={{ background: 'rgba(244,67,54,0.1)', color: '#F44336' }}
              >
                Eliminar
              </button>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-3 bg-gray-50">
          <button 
            onClick={onClose} 
            disabled={isSaving}
            className="px-4 py-2 border border-input rounded-lg hover:bg-muted transition font-semibold text-sm disabled:opacity-50"
          >
            Cancelar
          </button>
          <button 
            onClick={handleSaveClick}
            disabled={isSaving}
            className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-semibold shadow-sm transition text-sm disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {isSaving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
